import React, { useState, useEffect, useMemo } from 'react';
import { 
  Typography, 
  Box, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  Link,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getAllFixVersions, getWorkItems, getProductionBugs, getInternalBugs, generateReleaseNotes, deleteReleaseNotes, fetchWorkItemsByFixVersion } from '../../services/apiService';
import ReleaseEditDialog from './ReleaseEditDialog';
import ReleaseNotesEditor from './ReleaseNotesEditor';
import ReleaseComparisonCharts from './ReleaseComparisonCharts';
import { generateSummary } from '../../utils/textSummarizer';
import { formatDate } from '../../utils/formatters';
import FeatureChangesTable from './FeatureChangesTable';
import BugsTable from './BugsTable';
import Loading from '../common/Loading';

/**
 * ReleasesView component for displaying work items by release (fix version)
 * @returns {JSX.Element} ReleasesView component
 */
const ReleasesView = () => {
  // State for fix versions
  const [fixVersions, setFixVersions] = useState([]);
  const [fixVersionDetails, setFixVersionDetails] = useState({});
  const [selectedFixVersion, setSelectedFixVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for work items
  const [workItems, setWorkItems] = useState([]);
  const [productionBugs, setProductionBugs] = useState([]);
  const [internalBugs, setInternalBugs] = useState([]);
  const [loadingWorkItems, setLoadingWorkItems] = useState(false);
  const [fetchingLatest, setFetchingLatest] = useState(false);
  
  // State for release notes
  const [releaseNotesEditorOpen, setReleaseNotesEditorOpen] = useState(false);
  const [releaseNotesItems, setReleaseNotesItems] = useState([]);
  const [generatingReleaseNotes, setGeneratingReleaseNotes] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // State for release edit dialog
  const [releaseEditDialogOpen, setReleaseEditDialogOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  
  // Filter fix versions to only include those with format 5.9.08 and above
  const isValidFixVersion = (version) => {
    // Match pattern like 5.9.08 and ensure it's 5.9.08 or higher
    const regex = /^(\d+)\.(\d+)\.(\d+)$/;
    const match = version.match(regex);
    if (!match) return false;
    
    const [, major, minor, patch] = match;
    // Check if it's 5.9.08 or higher
    return (
      parseInt(major) > 5 || 
      (parseInt(major) === 5 && parseInt(minor) > 9) || 
      (parseInt(major) === 5 && parseInt(minor) === 9 && parseInt(patch) >= 8)
    );
  };
  
  // Filtered fix versions
  const filteredFixVersions = useMemo(() => {
    return fixVersions.filter(isValidFixVersion);
  }, [fixVersions]);
  
  // Function to organize and filter work items (same logic as in FeatureChangesTable)
  const organizeWorkItems = (items, targetFixVersion) => {
    const result = [];
    
    // Group items by their type
    const epics = items.filter(item => item.workItemType === 'Epic');
    const features = items.filter(item => item.workItemType === 'Feature');
    const pbis = items.filter(item => 
      item.workItemType === 'Product Backlog Item' || 
      item.workItemType === 'Task'
      // Exclude all bugs from feature changes
    );
    
    // Rule 1: If Epic fix version matches, list just Epic
    const matchingEpics = epics.filter(epic => 
      epic.fixVersion === targetFixVersion
    );
    
    // Store matching epic IDs for later use
    const matchingEpicIds = matchingEpics.map(epic => epic.id);
    
    matchingEpics.forEach(epic => {
      result.push({
        ...epic,
        level: 'Epic',
        children: []
      });
    });
    
    // Rule 2: If Feature fix version matches but its epic fix version doesn't match, list just Feature
    const epicIds = matchingEpics.map(epic => epic.id);
    const matchingFeatures = features.filter(feature => 
      feature.fixVersion === targetFixVersion && 
      !epicIds.includes(feature.parent)
    );
    
    matchingFeatures.forEach(feature => {
      result.push({
        ...feature,
        level: 'Feature',
        children: []
      });
    });
    
    // Rule 3: If Epic and Feature don't match, and only PBI fix version matches, list only PBI
    // First, collect all feature IDs (both matching and non-matching)
    const allFeatureIds = features.map(feature => feature.id);
    
    // Get all parent IDs of matching features
    const matchingFeatureParentIds = matchingFeatures.map(feature => feature.parent);
    
    // Get all feature IDs (both matching and non-matching)
    const featureIds = matchingFeatures.map(feature => feature.id);
    
    // Find PBIs that match the fix version AND:
    // 1. Don't have a parent that is a matching feature (to avoid showing PBIs under matching features)
    // 2. Don't have a parent that is a feature whose parent is a matching epic (to avoid showing PBIs under features that are under matching epics)
    const matchingPbis = pbis.filter(pbi => {
      // PBI must match the fix version
      if (pbi.fixVersion !== targetFixVersion) return false;
      
      // If PBI's parent is a matching feature, don't include it
      if (featureIds.includes(pbi.parent)) return false;
      
      // If PBI's parent is a feature whose parent is a matching epic, don't include it
      const parentFeature = features.find(f => f.id === pbi.parent);
      if (parentFeature && matchingEpicIds.includes(parentFeature.parent)) return false;
      
      // Otherwise, include the PBI
      return true;
    });
    
    matchingPbis.forEach(pbi => {
      result.push({
        ...pbi,
        level: 'PBI',
        children: []
      });
    });
    
    return result;
  };
  
  // Handle create release
  const handleCreateRelease = () => {
    setEditingRelease(null);
    setReleaseEditDialogOpen(true);
  };
  
  // Handle edit release
  const handleEditRelease = () => {
    if (!selectedFixVersion || !fixVersionDetails[selectedFixVersion]) {
      setSnackbarMessage('No release selected');
      setSnackbarOpen(true);
      return;
    }
    
    setEditingRelease(fixVersionDetails[selectedFixVersion]);
    setReleaseEditDialogOpen(true);
  };
  
  // Handle save release
  const handleSaveRelease = async (savedRelease) => {
    try {
      // Refresh fix versions
      const response = await getAllFixVersions();
      
      if (response && response.data && Array.isArray(response.data)) {
        const activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
        
        const versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
        const versionDetailsMap = {};
        activeVersionsData.forEach(fixVersion => {
          versionDetailsMap[fixVersion.version] = fixVersion;
        });
        
        setFixVersions(versionStrings);
        setFixVersionDetails(versionDetailsMap);
        
        // If this was a new release, select it
        if (savedRelease && savedRelease.version && !selectedFixVersion) {
          setSelectedFixVersion(savedRelease.version);
        }
        
        setSnackbarMessage('Release saved successfully!');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error refreshing fix versions:', error);
      setSnackbarMessage('Release saved, but failed to refresh the list. Please reload the page.');
      setSnackbarOpen(true);
    }
  };
  
  // Handle generate release notes
  const handleGenerateReleaseNotes = () => {
    try {
      setLoadingWorkItems(true);
      
      // Get the filtered work items using the same logic as FeatureChangesTable
      const filteredWorkItems = organizeWorkItems(workItems, selectedFixVersion);
      
      // Combine filtered work items with bugs, adding a source field to track where each bug came from
      const allItems = [
        ...filteredWorkItems,
        ...productionBugs.map(bug => ({ ...bug, bugSource: 'production' })),
        ...internalBugs.map(bug => ({ ...bug, bugSource: 'internal' }))
      ];
      
      // Generate summaries for each item to show in the editor
      const itemsWithSummaries = allItems.map(item => {
        // Create a copy of the item with a generated summary
        return {
          ...item,
          summary: generateSummary(item)
        };
      });
      
      setReleaseNotesItems(itemsWithSummaries);
      setReleaseNotesEditorOpen(true);
    } catch (error) {
      console.error('Error preparing release notes:', error);
      setSnackbarMessage('Failed to prepare release notes. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoadingWorkItems(false);
    }
  };
  
  // Handle approve release notes
  const handleApproveReleaseNotes = async (items) => {
    try {
      setGeneratingReleaseNotes(true);
      
      // Get the fix version ID
      const fixVersionId = fixVersionDetails[selectedFixVersion]?._id;
      
      if (!fixVersionId) {
        console.error('Fix version ID not found for version:', selectedFixVersion);
        console.error('Available fix version details:', fixVersionDetails);
        throw new Error(`Fix version ID not found for version: ${selectedFixVersion}`);
      }
      
      console.log(`Generating release notes for fix version: ${selectedFixVersion}, ID: ${fixVersionId}`);
      
      // Send items to backend to generate HTML and save file
      // Include version string in body as primary identifier (more reliable than ID)
      // Use a placeholder ID if we don't have one, since backend will use version string
      const idToUse = fixVersionId || 'unknown';
      const response = await generateReleaseNotes(idToUse, items, selectedFixVersion);
      
      if (response && response.success) {
        // Update the fix version details with the release notes URL
        setFixVersionDetails(prev => ({
          ...prev,
          [selectedFixVersion]: {
            ...prev[selectedFixVersion],
            releaseNotesUrl: response.data.releaseNotesUrl
          }
        }));
        
        setSnackbarMessage('Release notes generated successfully!');
        setSnackbarOpen(true);
        setReleaseNotesEditorOpen(false);
      } else {
        throw new Error('Failed to generate release notes');
      }
    } catch (error) {
      console.error('Error generating release notes:', error);
      setSnackbarMessage('Failed to generate release notes. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setGeneratingReleaseNotes(false);
    }
  };
  
  // Fetch fix versions on component mount
  useEffect(() => {
    const fetchFixVersions = async () => {
      try {
        setLoading(true);
        const response = await getAllFixVersions();
        
        console.log('Fix versions response:', response);
        
        // Check for different response formats
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          // Nested data structure (response.data.data is an array)
          console.log('Processing fix versions as nested data structure');
          const activeVersionsData = response.data.data.filter(fixVersion => fixVersion.active);
          
          const versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
          const versionDetailsMap = {};
          activeVersionsData.forEach(fixVersion => {
            versionDetailsMap[fixVersion.version] = fixVersion;
          });
          
          setFixVersions(versionStrings);
          setFixVersionDetails(versionDetailsMap);
        } else if (response && response.data && Array.isArray(response.data)) {
          // Direct array in data property
          console.log('Processing fix versions as direct array in data property');
          const activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
          
          const versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
          const versionDetailsMap = {};
          activeVersionsData.forEach(fixVersion => {
            versionDetailsMap[fixVersion.version] = fixVersion;
          });
          
          setFixVersions(versionStrings);
          setFixVersionDetails(versionDetailsMap);
        } else if (response && Array.isArray(response)) {
          // Direct array response
          console.log('Processing fix versions as direct array response');
          const activeVersionsData = response.filter(fixVersion => fixVersion.active);
          
          const versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
          const versionDetailsMap = {};
          activeVersionsData.forEach(fixVersion => {
            versionDetailsMap[fixVersion.version] = fixVersion;
          });
          
          setFixVersions(versionStrings);
          setFixVersionDetails(versionDetailsMap);
        } else if (response && response.success && Array.isArray(response.data)) {
          // Success object with data array
          console.log('Processing fix versions as success object with data array');
          const activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
          
          const versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
          const versionDetailsMap = {};
          activeVersionsData.forEach(fixVersion => {
            versionDetailsMap[fixVersion.version] = fixVersion;
          });
          
          setFixVersions(versionStrings);
          setFixVersionDetails(versionDetailsMap);
        } else {
          console.error('Invalid fix versions response:', response);
          setError('Failed to load fix versions. Invalid response format.');
          setFixVersions([]); // Initialize with empty array to prevent map errors
          setFixVersionDetails({});
        }
      } catch (err) {
        console.error('Error fetching fix versions:', err);
        setError('Failed to load fix versions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFixVersions();
  }, []);
  
  // Fetch work items when fix version changes
  useEffect(() => {
    const fetchWorkItems = async () => {
      if (!selectedFixVersion) {
        setWorkItems([]);
        setProductionBugs([]);
        setInternalBugs([]);
        return;
      }
      
      try {
        setLoadingWorkItems(true);
        setError(null);
        
        // Fetch work items, production bugs, and internal bugs in parallel
        const [workItemsResponse, productionBugsResponse, internalBugsResponse] = await Promise.all([
          getWorkItems({ fixVersion: selectedFixVersion }),
          getProductionBugs({ fixVersion: selectedFixVersion }),
          getInternalBugs({ fixVersion: selectedFixVersion })
        ]);
        
        console.log('Raw work items response:', workItemsResponse);
        console.log('Raw production bugs response:', productionBugsResponse);
        console.log('Raw internal bugs response:', internalBugsResponse);
        
        // Process work items
        try {
          if (workItemsResponse && workItemsResponse.data) {
            // Direct array response
            if (Array.isArray(workItemsResponse.data)) {
              console.log('Work items found (direct array):', workItemsResponse.data.length);
              setWorkItems(workItemsResponse.data);
            }
            // Success object with data array
            else if (workItemsResponse.data.success && Array.isArray(workItemsResponse.data.data)) {
              console.log('Work items found (success object):', workItemsResponse.data.data.length);
              setWorkItems(workItemsResponse.data.data);
            }
            // Direct data array
            else if (Array.isArray(workItemsResponse.data.data)) {
              console.log('Work items found (direct data array):', workItemsResponse.data.data.length);
              setWorkItems(workItemsResponse.data.data);
            }
            else {
              console.error('Unrecognized work items response format:', workItemsResponse);
              setWorkItems([]);
            }
          } else {
            console.error('Invalid work items response:', workItemsResponse);
            setWorkItems([]);
          }
        } catch (err) {
          console.error('Error processing work items response:', err);
          setWorkItems([]);
        }
        
        // Process production bugs
        try {
          if (productionBugsResponse && productionBugsResponse.data) {
            // Direct array response
            if (Array.isArray(productionBugsResponse.data)) {
              console.log('Production bugs found (direct array):', productionBugsResponse.data.length);
              setProductionBugs(productionBugsResponse.data);
            }
            // Success object with data array
            else if (productionBugsResponse.data.success && Array.isArray(productionBugsResponse.data.data)) {
              console.log('Production bugs found (success object):', productionBugsResponse.data.data.length);
              setProductionBugs(productionBugsResponse.data.data);
            }
            // Direct data array
            else if (Array.isArray(productionBugsResponse.data.data)) {
              console.log('Production bugs found (direct data array):', productionBugsResponse.data.data.length);
              setProductionBugs(productionBugsResponse.data.data);
            }
            else {
              console.error('Unrecognized production bugs response format:', productionBugsResponse);
              setProductionBugs([]);
            }
          } else {
            console.error('Invalid production bugs response:', productionBugsResponse);
            setProductionBugs([]);
          }
        } catch (err) {
          console.error('Error processing production bugs response:', err);
          setProductionBugs([]);
        }
        
        // Process internal bugs
        try {
          if (internalBugsResponse && internalBugsResponse.data) {
            // Direct array response
            if (Array.isArray(internalBugsResponse.data)) {
              console.log('Internal bugs found (direct array):', internalBugsResponse.data.length);
              setInternalBugs(internalBugsResponse.data);
            }
            // Success object with data array
            else if (internalBugsResponse.data.success && Array.isArray(internalBugsResponse.data.data)) {
              console.log('Internal bugs found (success object):', internalBugsResponse.data.data.length);
              setInternalBugs(internalBugsResponse.data.data);
            }
            // Direct data array
            else if (Array.isArray(internalBugsResponse.data.data)) {
              console.log('Internal bugs found (direct data array):', internalBugsResponse.data.data.length);
              setInternalBugs(internalBugsResponse.data.data);
            }
            else {
              console.error('Unrecognized internal bugs response format:', internalBugsResponse);
              setInternalBugs([]);
            }
          } else {
            console.error('Invalid internal bugs response:', internalBugsResponse);
            setInternalBugs([]);
          }
        } catch (err) {
          console.error('Error processing internal bugs response:', err);
          setInternalBugs([]);
        }
      } catch (err) {
        console.error('Error fetching work items:', err);
        setError('Failed to load work items. Please try again later.');
      } finally {
        setLoadingWorkItems(false);
      }
    };
    
    fetchWorkItems();
  }, [selectedFixVersion]);
  
  // Handle fix version change
  const handleFixVersionChange = (event) => {
    setSelectedFixVersion(event.target.value);
  };
  
  // Handle get latest from ADO
  const handleGetLatest = async () => {
    if (!selectedFixVersion) {
      setSnackbarMessage('Please select a release first');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setFetchingLatest(true);
      setError(null);
      
      const response = await fetchWorkItemsByFixVersion(selectedFixVersion);
      
      if (response && response.success) {
        setSnackbarMessage(
          `Successfully fetched ${response.data.total} work items (${response.data.created} created, ${response.data.updated} updated)`
        );
        setSnackbarOpen(true);
        
        // Refresh work items after fetching
        const [workItemsResponse, productionBugsResponse, internalBugsResponse] = await Promise.all([
          getWorkItems({ fixVersion: selectedFixVersion }),
          getProductionBugs({ fixVersion: selectedFixVersion }),
          getInternalBugs({ fixVersion: selectedFixVersion })
        ]);
        
        // Process responses (same logic as in useEffect)
        if (workItemsResponse && workItemsResponse.data) {
          if (Array.isArray(workItemsResponse.data)) {
            setWorkItems(workItemsResponse.data);
          } else if (workItemsResponse.data.success && Array.isArray(workItemsResponse.data.data)) {
            setWorkItems(workItemsResponse.data.data);
          } else if (Array.isArray(workItemsResponse.data.data)) {
            setWorkItems(workItemsResponse.data.data);
          } else {
            setWorkItems([]);
          }
        } else {
          setWorkItems([]);
        }
        
        if (productionBugsResponse && productionBugsResponse.data) {
          if (Array.isArray(productionBugsResponse.data)) {
            setProductionBugs(productionBugsResponse.data);
          } else if (productionBugsResponse.data.success && Array.isArray(productionBugsResponse.data.data)) {
            setProductionBugs(productionBugsResponse.data.data);
          } else if (Array.isArray(productionBugsResponse.data.data)) {
            setProductionBugs(productionBugsResponse.data.data);
          } else {
            setProductionBugs([]);
          }
        } else {
          setProductionBugs([]);
        }
        
        if (internalBugsResponse && internalBugsResponse.data) {
          if (Array.isArray(internalBugsResponse.data)) {
            setInternalBugs(internalBugsResponse.data);
          } else if (internalBugsResponse.data.success && Array.isArray(internalBugsResponse.data.data)) {
            setInternalBugs(internalBugsResponse.data.data);
          } else if (Array.isArray(internalBugsResponse.data.data)) {
            setInternalBugs(internalBugsResponse.data.data);
          } else {
            setInternalBugs([]);
          }
        } else {
          setInternalBugs([]);
        }
      } else {
        throw new Error(response?.message || 'Failed to fetch work items');
      }
    } catch (error) {
      console.error('Error fetching latest work items:', error);
      setError(error.message || 'Failed to fetch work items from ADO. Please try again later.');
      setSnackbarMessage(error.message || 'Failed to fetch work items from ADO. Please try again later.');
      setSnackbarOpen(true);
    } finally {
      setFetchingLatest(false);
    }
  };
  
  // Handle view release notes
  const handleViewReleaseNotes = (url) => {
    if (url.startsWith('OPEN_NEW_TAB:')) {
      // Extract the actual URL from the special format
      const actualUrl = url.replace('OPEN_NEW_TAB:', '');
      // Open in a new tab
      window.open(actualUrl, '_blank');
    } else {
      // Fallback to normal URL
      window.open(url, '_blank');
    }
  };
  
  // Handle delete release notes
  const handleDeleteReleaseNotes = async () => {
    try {
      if (!selectedFixVersion || !fixVersionDetails[selectedFixVersion]) {
        setSnackbarMessage('No fix version selected');
        setSnackbarOpen(true);
        return;
      }
      
      const fixVersionId = fixVersionDetails[selectedFixVersion]._id;
      
      if (!fixVersionId) {
        setSnackbarMessage('Fix version ID not found');
        setSnackbarOpen(true);
        return;
      }
      
      // Confirm deletion
      if (!window.confirm('Are you sure you want to delete these release notes? This action cannot be undone.')) {
        return;
      }
      
      setLoadingWorkItems(true);
      
      // Delete release notes
      const response = await deleteReleaseNotes(fixVersionId);
      
      if (response && response.success) {
        // Update the fix version details to remove the release notes URL
        setFixVersionDetails(prev => ({
          ...prev,
          [selectedFixVersion]: {
            ...prev[selectedFixVersion],
            releaseNotesUrl: null
          }
        }));
        
        setSnackbarMessage('Release notes deleted successfully!');
        setSnackbarOpen(true);
      } else {
        throw new Error('Failed to delete release notes');
      }
    } catch (error) {
      console.error('Error deleting release notes:', error);
      setSnackbarMessage('Failed to delete release notes. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoadingWorkItems(false);
    }
  };
  
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Releases
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View work items and bugs by release (fix version)
        </Typography>
      </Box>
      
      {loading ? (
        <Loading message="Loading fix versions..." />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Fix Version Selector */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl sx={{ width: '40%' }}>
                <InputLabel id="fix-version-select-label">Select Release (Fix Version)</InputLabel>
                <Select
                  labelId="fix-version-select-label"
                  id="fix-version-select"
                  value={selectedFixVersion}
                  label="Select Release (Fix Version)"
                  onChange={handleFixVersionChange}
                >
                  <MenuItem value="">
                    <em>Select a release</em>
                  </MenuItem>
                  {filteredFixVersions.map((version) => (
                    <MenuItem key={version} value={version}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Typography sx={{ flexGrow: 1 }}>{version}</Typography>
                        {fixVersionDetails[version]?.isMaintenanceRelease && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              bgcolor: '#ffecb3', 
                              color: '#bf360c', 
                              px: 1, 
                              py: 0.2, 
                              borderRadius: 1,
                              fontWeight: 'bold',
                              ml: 1,
                              fontSize: '0.7rem'
                            }}
                          >
                            Maintenance
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {selectedFixVersion && (
                <Tooltip title="Edit Release">
                  <IconButton
                    color="primary"
                    onClick={handleEditRelease}
                    disabled={!selectedFixVersion}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {selectedFixVersion && (
                <>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    disabled={!selectedFixVersion || fetchingLatest}
                    onClick={handleGetLatest}
                    startIcon={fetchingLatest ? <CircularProgress size={16} /> : <RefreshIcon />}
                    sx={{ ml: 2 }}
                  >
                    {fetchingLatest ? 'Fetching...' : 'Get Latest'}
                  </Button>
                  
                  <Button 
                    variant="contained" 
                    color="primary"
                    disabled={!selectedFixVersion || !isValidFixVersion(selectedFixVersion) || loadingWorkItems}
                    onClick={handleGenerateReleaseNotes}
                    sx={{ ml: 2 }}
                  >
                    Generate Release Notes
                  </Button>
                  
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', ml: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'nowrap', mr: 2 }}>
                        <strong>Release Date:</strong> {
                          fixVersionDetails[selectedFixVersion] && 
                          fixVersionDetails[selectedFixVersion].releaseDate ? 
                          formatDate(fixVersionDetails[selectedFixVersion].releaseDate) : 
                          'Not available'
                        }
                      </Typography>
                      
                      {fixVersionDetails[selectedFixVersion]?.isMaintenanceRelease && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            bgcolor: '#ffecb3', 
                            color: '#bf360c', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontWeight: 'bold',
                            mr: 2
                          }}
                        >
                          Maintenance Release
                        </Typography>
                      )}
                    </Box>
                    
                    {fixVersionDetails[selectedFixVersion]?.releaseNotesUrl && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Link 
                          onClick={() => handleViewReleaseNotes(fixVersionDetails[selectedFixVersion].releaseNotesUrl)}
                          sx={{ cursor: 'pointer' }}
                          underline="hover"
                        >
                          View Release Notes
                        </Link>
                        <Button 
                          variant="outlined" 
                          color="error" 
                          size="small"
                          onClick={handleDeleteReleaseNotes}
                        >
                          Delete Release Notes
                        </Button>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Paper>
          
          {/* Work Items and Bugs Display */}
          {selectedFixVersion ? (
            loadingWorkItems ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Infographics Section - Work Item Counts */}
                <Box mb={4}>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h5" gutterBottom>
                      Release Summary
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      gap: 2,
                      mt: 2,
                      pb: 1
                    }}>
                      {/* Epic Count */}
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 2, 
                          minWidth: '150px',
                          flex: 1,
                          bgcolor: '#e3f2fd',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="h4" color="primary">
                          {workItems.filter(item => item.workItemType === 'Epic').length}
                        </Typography>
                        <Typography variant="subtitle1">
                          Epics
                        </Typography>
                      </Paper>
                      
                      {/* Feature Count */}
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 2, 
                          minWidth: '150px',
                          flex: 1,
                          bgcolor: '#e8f5e9',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="h4" color="success.main">
                          {workItems.filter(item => item.workItemType === 'Feature').length}
                        </Typography>
                        <Typography variant="subtitle1">
                          Features
                        </Typography>
                      </Paper>
                      
                      {/* PBI Count */}
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 2, 
                          minWidth: '150px',
                          flex: 1,
                          bgcolor: '#fff8e1',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="h4" color="warning.main">
                          {workItems.filter(item => 
                            item.workItemType === 'Product Backlog Item' || 
                            item.workItemType === 'Task'
                          ).length}
                        </Typography>
                        <Typography variant="subtitle1">
                          PBIs
                        </Typography>
                      </Paper>
                      
                      {/* Production Bugs Count */}
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 2, 
                          minWidth: '150px',
                          flex: 1,
                          bgcolor: '#ffebee',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="h4" color="error.main">
                          {productionBugs.length}
                        </Typography>
                        <Typography variant="subtitle1">
                          Production Bugs
                        </Typography>
                      </Paper>
                      
                      {/* Internal Bugs Count */}
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 2, 
                          minWidth: '150px',
                          flex: 1,
                          bgcolor: '#f3e5f5',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="h4" color="secondary.main">
                          {internalBugs.length}
                        </Typography>
                        <Typography variant="subtitle1">
                          Internal Bugs
                        </Typography>
                      </Paper>
                    </Box>
                  </Paper>
                </Box>
                {/* Feature Changes Section - only show if there are work items */}
                {workItems.length > 0 && (
                  <>
                    <Box mb={4}>
                      <Typography variant="h5" gutterBottom>
                        Feature Changes
                      </Typography>
                      <FeatureChangesTable workItems={workItems} fixVersion={selectedFixVersion} />
                    </Box>
                    <Divider sx={{ my: 4 }} />
                  </>
                )}
                
                {/* Production Bugs Section */}
                <Box mb={4}>
                  <Typography variant="h5" gutterBottom>
                    Production Bugs
                  </Typography>
                  {productionBugs.length > 0 ? (
                    <BugsTable
                      bugs={productionBugs}
                      bugType="production"
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No production bugs found for this release.
                    </Typography>
                  )}
                </Box>
                
                {/* Internal Bugs Section */}
                <Box mb={4}>
                  <Typography variant="h5" gutterBottom>
                    Internal Bugs
                  </Typography>
                  {internalBugs.length > 0 ? (
                    <BugsTable
                      bugs={internalBugs}
                      bugType="internal"
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No internal bugs found for this release.
                    </Typography>
                  )}
                </Box>
              </>
            )
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              Select a release to view work items and bugs.
            </Typography>
          )}
        </>
      )}
      
      {/* Release Edit Dialog */}
      <ReleaseEditDialog
        open={releaseEditDialogOpen}
        onClose={() => setReleaseEditDialogOpen(false)}
        onSave={handleSaveRelease}
        release={editingRelease}
      />
      
      {/* Release Notes Editor */}
      <ReleaseNotesEditor
        open={releaseNotesEditorOpen}
        onClose={() => setReleaseNotesEditorOpen(false)}
        onApprove={handleApproveReleaseNotes}
        items={releaseNotesItems}
        loading={generatingReleaseNotes}
        fixVersion={selectedFixVersion}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ReleasesView;
